"use client";

import Image from "next/image";
import { useRef, useState } from "react";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { AboutUsTeamCopy, AboutUsTeamMember } from "./about-us-copy";
import { CcUsLines, CcUsReveal } from "./CcUsReveal";
import { CcUsTypewriter } from "./CcUsText";

type CcUsTeamProps = {
  copy: AboutUsTeamCopy;
  /** Members to show (placeholders are filtered out in production by the view). */
  members: AboutUsTeamMember[];
  id: string;
};

/**
 * Team picker, modelled on Inversa's field crew: thin white line figures with
 * their names on the left; clicking one brings that person forward on the
 * right — the figure rises in, the text types out and a small "app window"
 * shows what they work on. No scrolling through the whole crew is needed.
 */
export function CcUsTeam({ copy, members, id }: CcUsTeamProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const count = members.length;

  const select = (index: number) => {
    setActive(index);
    const info = infoRef.current;
    // Side by side on wider screens: everything is already in view.
    if (!info || !window.matchMedia("(max-width: 810px)").matches) return;
    const rect = info.getBoundingClientRect();
    // On phones the text and figure sit below the picker: bring them into view.
    if (rect.top < 0 || rect.top > window.innerHeight * 0.6) {
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      info.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    }
  };

  if (!count) return null;
  const member = members[active];

  return (
    <section
      id={id}
      className="cc-us-team"
      aria-labelledby="cc-us-team-title"
      data-cc-nav="dark"
      {...devSectionAttr("about-team")}
    >
      <div className="cc-container cc-us-team__head">
        <CcSanjayaEyebrow theme="on-dark">{copy.label}</CcSanjayaEyebrow>
        <CcUsLines id="cc-us-team-title" className="cc-us-team__title" lines={copy.titleLines} />
        <p className="cc-us-team__intro">{copy.intro}</p>
      </div>

      <div className="cc-container cc-us-team__body">
        {/* Picker on the left: the whole crew with names. */}
        <div className="cc-us-team__side">
          <CcUsReveal as="ul" className="cc-us-team__lineup">
            {members.map((person, index) => (
              <li key={person.id} style={{ transitionDelay: `${index * 90}ms` }}>
                <button
                  type="button"
                  className="cc-us-team__pick"
                  onClick={() => select(index)}
                  aria-pressed={index === active}
                >
                  <Image src={person.image.src} alt="" width={900} height={1600} sizes="(min-width: 810px) 8vw, 22vw" />
                  <span className="cc-us-team__pick-name">{person.name.split(" ")[0]}</span>
                </button>
              </li>
            ))}
          </CcUsReveal>

          <div ref={infoRef} className="cc-us-team__info" key={member.id}>
            <CcSanjayaEyebrow theme="on-dark">{member.role}</CcSanjayaEyebrow>
            <h3 className="cc-us-team__name">{member.name}</h3>
            <CcUsTypewriter className="cc-us-team__text" text={member.text} play />
            {member.email ? (
              <a
                className="cc-us-team__email"
                href={`mailto:${member.email}`}
              >
                {member.email}
              </a>
            ) : null}
            {!member.published ? <p className="cc-us-team__draft">{copy.placeholderNote}</p> : null}
          </div>
        </div>

        {/* The chosen person on the right, with what they work on. */}
        <div ref={stageRef} className="cc-us-team__stage-wrap">
          <ul className="cc-us-team__stage" aria-hidden="true">
            {members.map((person, index) => (
              <li key={person.id} data-on={index === active ? "true" : "false"}>
                <Image src={person.image.src} alt="" width={900} height={1600} sizes="(min-width: 810px) 30vw, 70vw" />
              </li>
            ))}
          </ul>

          <div className="cc-us-team__windows" key={`${member.id}-windows`} aria-hidden="true">
            {member.windows.map((window) => (
              <div key={window.title} className="cc-us-window">
                <span className="cc-us-window__bar">
                  <span>● {window.title}</span>
                </span>
                <dl className="cc-us-window__rows">
                  {window.rows.map((row) => (
                    <div key={row.label}>
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>

        {/* The founders, quietly, under the team. */}
        <ul className="cc-us-team__founders">
          {copy.founders.members.map((founder) => (
            <li key={founder.id}>
              <span className="cc-us-team__founder-label">{copy.founders.label}</span>
              <span className="cc-us-team__founder-name">{founder.name}</span>
              <span className="cc-us-team__founder-role">{founder.role}</span>
              <span className="cc-us-team__founder-email">{founder.email}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Everyone as plain text too, for screen readers and search engines. */}
      <ul className="cc-us-sr">
        {members.map((person) => (
          <li key={person.id}>
            {person.name}, {person.role}: {person.text}
            {person.email ? ` ${person.email}` : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
